import { Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VideoIdeaCard = ({ title, description, gradient, height }) => {
  // Generate random background position avoiding corners (empty areas)
  const randomX = Math.floor(Math.random() * 60) + 20; // 20-80% range
  const randomY = Math.floor(Math.random() * 60) + 20; // 20-80% range
  
  return (
    <div className="group relative masonry-item" style={{ breakInside: 'avoid', marginBottom: '16px' }}>
      <div 
        className={`relative bg-gradient-to-br ${gradient} rounded-[20px] p-0.5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
        style={{ height: height }}
      >
        <div 
          className="h-full rounded-[18px] overflow-hidden p-4 flex flex-col justify-center items-center text-center relative"
          style={{
            backgroundSize: '300%',
            backgroundPosition: `${randomX}% ${randomY}%`
          }}
        >
          <div className={`absolute inset-0 rounded-[18px] opacity-15 bg-gradient-to-br ${gradient}`}></div>
          <div className="absolute inset-0 bg-white/85 dark:bg-slate-800/85 rounded-[18px]"></div>
          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 dark:from-amber-500 dark:to-yellow-600 rounded-lg shadow-md group-hover:scale-110 transition-transform duration-300 mb-3">
              <Lightbulb className="w-5 h-5 text-white" strokeWidth={2.5} fill="currentColor" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-tight mb-2">
              {title}
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const VideoIdeas = () => {
  const { t } = useTranslation();
  
  const videoIdeas = [
    {
      title: t('videoIdeas.ideas.yearRecap.title'),
      description: t('videoIdeas.ideas.yearRecap.description'),
      gradient: 'from-sky-400 to-blue-500',
      height: '220px'
    },
    {
      title: t('videoIdeas.ideas.europeRoadTrip.title'),
      description: t('videoIdeas.ideas.europeRoadTrip.description'),
      gradient: 'from-purple-400 to-indigo-500',
      height: '180px'
    },
    {
      title: t('videoIdeas.ideas.southeastAsia.title'),
      description: t('videoIdeas.ideas.southeastAsia.description'),
      gradient: 'from-emerald-400 to-teal-500',
      height: '240px'
    },
    {
      title: t('videoIdeas.ideas.japanRail.title'),
      description: t('videoIdeas.ideas.japanRail.description'),
      gradient: 'from-pink-400 to-rose-500',
      height: '200px'
    },
    {
      title: t('videoIdeas.ideas.crossCountry.title'),
      description: t('videoIdeas.ideas.crossCountry.description'),
      gradient: 'from-amber-400 to-orange-500',
      height: '230px'
    },
    {
      title: t('videoIdeas.ideas.islandHopping.title'),
      description: t('videoIdeas.ideas.islandHopping.description'),
      gradient: 'from-cyan-400 to-blue-500',
      height: '190px'
    },
    {
      title: t('videoIdeas.ideas.worldTour.title'),
      description: t('videoIdeas.ideas.worldTour.description'),
      gradient: 'from-violet-400 to-purple-500',
      height: '210px'
    },
    {
      title: t('videoIdeas.ideas.travelPlans.title'),
      description: t('videoIdeas.ideas.travelPlans.description'),
      gradient: 'from-fuchsia-400 to-pink-500',
      height: '195px'
    },
    {
      title: t('videoIdeas.ideas.lifetimeTravel.title'),
      description: t('videoIdeas.ideas.lifetimeTravel.description'),
      gradient: 'from-slate-400 to-gray-500',
      height: '225px'
    },
    {
      title: t('videoIdeas.ideas.regionalExplorer.title'),
      description: t('videoIdeas.ideas.regionalExplorer.description'),
      gradient: 'from-red-400 to-orange-500',
      height: '185px'
    },
    {
      title: t('videoIdeas.ideas.festivalCircuit.title'),
      description: t('videoIdeas.ideas.festivalCircuit.description'),
      gradient: 'from-rose-400 to-pink-500',
      height: '205px'
    },
    {
      title: t('videoIdeas.ideas.bikeRoutes.title'),
      description: t('videoIdeas.ideas.bikeRoutes.description'),
      gradient: 'from-indigo-400 to-blue-500',
      height: '235px'
    }
  ];

  // Distribute cards into columns with EQUAL total heights (masonry with aligned bottom)
  const distributeIntoColumns = (items, numColumns) => {
    const columns = Array.from({ length: numColumns }, () => []);
    const columnHeights = Array(numColumns).fill(0);
    
    // Sort items by height (descending) for better distribution
    const sortedItems = [...items].sort((a, b) => parseInt(b.height) - parseInt(a.height));
    
    sortedItems.forEach((item) => {
      // Always add to the shortest column
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      columns[shortestColumnIndex].push(item);
      columnHeights[shortestColumnIndex] += parseInt(item.height);
    });
    
    // Calculate max height for alignment
    const maxHeight = Math.max(...columnHeights);
    
    return { columns, maxHeight };
  };

  const { columns: desktopColumns, maxHeight: desktopHeight } = distributeIntoColumns(videoIdeas, 6);
  const { columns: tabletColumns, maxHeight: tabletHeight } = distributeIntoColumns(videoIdeas, 6);
  const { columns: smallTabletColumns, maxHeight: smallTabletHeight } = distributeIntoColumns(videoIdeas, 4);
  const { columns: mobileColumns, maxHeight: mobileHeight } = distributeIntoColumns(videoIdeas, 3);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            {t('videoIdeas.title')}
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            {t('videoIdeas.subtitle')}
          </p>
        </div>

        {/* Mobile: 3 columns */}
        <div className="flex md:hidden gap-4 justify-center" style={{ height: `${mobileHeight}px` }}>
          {mobileColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex flex-col justify-between" style={{ height: `${mobileHeight}px` }}>
              {column.map((idea, cardIndex) => (
                <VideoIdeaCard
                  key={`${columnIndex}-${cardIndex}`}
                  title={idea.title}
                  description={idea.description}
                  gradient={idea.gradient}
                  height={idea.height}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Tablet: 4 columns */}
        <div className="hidden md:flex lg:hidden gap-4 justify-center" style={{ height: `${smallTabletHeight}px` }}>
          {smallTabletColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex flex-col justify-between" style={{ height: `${smallTabletHeight}px` }}>
              {column.map((idea, cardIndex) => (
                <VideoIdeaCard
                  key={`${columnIndex}-${cardIndex}`}
                  title={idea.title}
                  description={idea.description}
                  gradient={idea.gradient}
                  height={idea.height}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Large Tablet: 6 columns */}
        <div className="hidden lg:flex xl:hidden gap-4 justify-center max-w-5xl mx-auto" style={{ height: `${tabletHeight}px` }}>
          {tabletColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex flex-col justify-between" style={{ height: `${tabletHeight}px` }}>
              {column.map((idea, cardIndex) => (
                <VideoIdeaCard
                  key={`${columnIndex}-${cardIndex}`}
                  title={idea.title}
                  description={idea.description}
                  gradient={idea.gradient}
                  height={idea.height}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Desktop: 6 columns */}
        <div className="hidden xl:flex gap-4 justify-center max-w-5xl mx-auto" style={{ height: `${desktopHeight}px` }}>
          {desktopColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex flex-col justify-between" style={{ height: `${desktopHeight}px` }}>
              {column.map((idea, cardIndex) => (
                <VideoIdeaCard
                  key={`${columnIndex}-${cardIndex}`}
                  title={idea.title}
                  description={idea.description}
                  gradient={idea.gradient}
                  height={idea.height}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="text-center mt-16 md:mt-12">
          <p className="text-slate-600 dark:text-slate-300 max-w-3xl mx-auto text-lg">
            {t('videoIdeas.description')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default VideoIdeas;
